import React, { useEffect, useState, useCallback } from "react";
import { useUserAuth } from "../../UserAuth";
import { db } from "../../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getTodayNormalized, normalizeToStartOfDay } from "../../utils/dateUtils";
import { shouldShowCooperativeStreak } from "../../utils/visual_utils";
import { Icon } from "../Icon";

// Trophy icons now handled with emoji in getTrophyIcon function

export const ChallengeLeaderboard = ({
  challengeId,
  challengeName,
  challengePortions,
  isStandalone = true,
  challengeType = "leaderboard",
  goalPercent = null,
  datesToChapters = [],
  isChallengeAdmin = false,
}) => {
  const [rankings, setRankings] = useState([]);
  const [loadedChallenges, setLoadedChallenges] = useState(false);
  const [isLoadingRankings, setIsLoadingRankings] = useState(false);
  const { user } = useUserAuth();
  const isCooperative = challengeType === "cooperative";
  const totalPortions = challengePortions || datesToChapters.length;
  const titleText = isCooperative ? "Progress" : "Leaderboard";
  const cacheKey = `leaderboard_${challengeId}_${challengeType || "leaderboard"}`;

  const cacheRankings = useCallback((rankingsData) => {
    try {
      const cacheData = {
        rankings: rankingsData,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching rankings:', error);
    }
  }, [cacheKey]);

  const loadCachedRankings = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const { rankings: cachedRankings, timestamp } = JSON.parse(cachedData);
        if (Date.now() - timestamp < 2 * 60 * 1000) {
          setRankings(cachedRankings);
          setLoadedChallenges(true);
        }
      }
    } catch (error) {
      console.error('Error loading cached rankings:', error);
    }
  }, [cacheKey]);

  const FetchRankings = useCallback(async (retryCount = 0) => {
    if (user === null || challengeId === null) {
      return;
    }
    
    const maxRetries = 3;
    setIsLoadingRankings(true);
    
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 7000)
      );

      const challengeUsersRef = collection(
        db,
        "challenges",
        challengeId,
        "users"
      );
      const fetchPromise = getDocs(challengeUsersRef);
      const challengeUsersDoc = await Promise.race([fetchPromise, timeoutPromise]);

      const challengeRanking = await Promise.all(
        challengeUsersDoc.docs.map(async (userDoc) => {
          const userId = userDoc.id;

          const [userProgressDoc, userNameDoc] = await Promise.all([
            getDoc(doc(db, "users", userId, "challenges", challengeId)),
            getDoc(doc(db, "users", userId)),
          ]);

          const binaryString = userProgressDoc.data()?.progress || "";
          const lastUpdated = userProgressDoc.data().lastUpdated
            ? userProgressDoc.data().lastUpdated
            : 0;
          const sum = Array.from(binaryString.slice(0, totalPortions)).reduce(
            (acc, digit) => (digit === "1" ? acc + 1 : acc),
            0
          );

          const percentage = totalPortions
            ? Math.round((sum / totalPortions) * 100)
            : 0;

          return {
            userId,
            progress: sum,
            lastUpdated: lastUpdated,
            progressString: binaryString,
            name:
              userNameDoc.data() != null
                ? userNameDoc.data().displayName
                : "Unnamed User",
            percentage,
          };
        })
      );

      if (!isCooperative) {
        challengeRanking.sort((a, b) => {
          if (a.progress !== b.progress) {
            return b.progress - a.progress;
          }
          return a.lastUpdated - b.lastUpdated;
        });
      } else {
        challengeRanking.sort((a, b) => {
          const nameA = (a.name || "").trim();
          const nameB = (b.name || "").trim();
          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });
      }

      setRankings(challengeRanking);
      setLoadedChallenges(true);
      
      // Cache the results
      cacheRankings(challengeRanking);
    } catch (error) {
      console.error('Error fetching rankings:', error);
      
      if (retryCount < maxRetries) {
        console.log(`Retrying FetchRankings (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          FetchRankings(retryCount + 1);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
      } else {
        // Use cached data if available, otherwise show error
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { rankings: cachedRankings } = JSON.parse(cachedData);
            setRankings(cachedRankings);
            setLoadedChallenges(true);
          } catch (cacheError) {
            console.error('Error parsing cached rankings:', cacheError);
          }
        }
      }
    } finally {
      setIsLoadingRankings(false);
    }
  }, [user, challengeId, totalPortions, isCooperative, cacheKey, cacheRankings]);

  useEffect(() => {
    if (challengeId && user) {
      loadCachedRankings();
      FetchRankings();
    }
  }, [challengeId, user, challengeType, FetchRankings, loadCachedRankings]);


  const getTrophyIcon = (rank) => {
    switch (rank) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return null;
    }
  };

  const getRankDisplay = (rank) => {
    if (rank < 3) return getTrophyIcon(rank);
    return `#${rank + 1}`;
  };

  if (!user) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ 
          padding: '16px', 
          textAlign: 'center',
          color: 'var(--md-sys-color-on-surface-variant)'
        }}>
          <Icon name="signIn" size="hero" style={{ marginBottom: '16px' }} />
          <p style={{ margin: '0', fontSize: '16px' }}>
            Log in to view {titleText.toLowerCase()}!
          </p>
        </div>
      </>
    );
  }

  if (!loadedChallenges && !isLoadingRankings) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <md-circular-progress indeterminate></md-circular-progress>
        <p style={{ marginTop: '16px', color: 'var(--md-sys-color-on-surface-variant)' }}>
          Loading {titleText.toLowerCase()}...
        </p>
      </div>
    );
  }

  const today = getTodayNormalized();
  let lastScheduledIndex = -1;
  datesToChapters.forEach((dateToChapter, index) => {
    const normalizedDate = normalizeToStartOfDay(dateToChapter[0]);
    if (normalizedDate <= today) {
      lastScheduledIndex = index;
    }
  });

  const totalMembers = rankings.length;
  const totalCompleted = rankings.reduce((sum, participant) => {
    const progressString = participant.progressString || "";
    const completed = Array.from(progressString.slice(0, totalPortions)).reduce(
      (acc, digit) => (digit === "1" ? acc + 1 : acc),
      0
    );
    return sum + completed;
  }, 0);
  const overallPercent = totalMembers && totalPortions
    ? Math.round((totalCompleted / (totalMembers * totalPortions)) * 100)
    : 0;

  const expectedDaysCount = lastScheduledIndex >= 0 ? lastScheduledIndex + 1 : 0;
  const completedToDate = rankings.reduce((sum, participant) => {
    const progressString = participant.progressString || "";
    const completed = Array.from(progressString.slice(0, expectedDaysCount)).reduce(
      (acc, digit) => (digit === "1" ? acc + 1 : acc),
      0
    );
    return sum + completed;
  }, 0);
  const predictionPercent = totalMembers && expectedDaysCount
    ? Math.round((completedToDate / (totalMembers * expectedDaysCount)) * 100)
    : 0;

  const containerStyle = isStandalone ? {
    height: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column'
  } : {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  };

  const contentStyle = isStandalone ? {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    padding: '24px 16px'
  } : {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    padding: '16px'
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={containerStyle}>
        {/* Header - only show if standalone */}
        {isStandalone && (
          <div style={{ 
            textAlign: 'center', 
            padding: '24px 16px 0 16px',
            flexShrink: 0
          }}>
            <h1 style={{ 
              fontSize: 'var(--md-sys-typescale-headline-large-size)',
              fontWeight: 'var(--md-sys-typescale-headline-large-weight)',
              color: 'var(--md-sys-color-on-surface)',
              margin: '0 0 8px 0'
            }}>
              {titleText}
            </h1>
            {challengeName && (
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                color: 'var(--md-sys-color-on-surface-variant)'
              }}>
                {challengeName}
              </p>
            )}
          </div>
        )}

        {/* Tab header - only show if not standalone */}
        {!isStandalone && challengeName && (
          <div style={{ 
            textAlign: 'center', 
            padding: '16px 16px 8px 16px',
            flexShrink: 0
          }}>
            <h2 style={{ 
              fontSize: 'var(--md-sys-typescale-headline-medium-size)',
              fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
              color: 'var(--md-sys-color-on-surface)',
              margin: '0'
            }}>
              {challengeName}
            </h2>
          </div>
        )}

        {/* Scrollable Content */}
        <div style={contentStyle}>
        {/* Loading indicator for background refresh */}
        {isLoadingRankings && loadedChallenges && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '8px',
            backgroundColor: 'var(--md-sys-color-surface-container-low)',
            borderRadius: '8px',
            fontSize: '14px',
            color: 'var(--md-sys-color-on-surface-variant)'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid currentColor',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Updating {titleText.toLowerCase()}...
          </div>
        )}

        {isCooperative && (
          <md-elevated-card style={{ padding: '16px', marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500' }}>
                Group Progress
              </div>
              <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '14px' }}>
                Goal: {Number.isFinite(goalPercent) ? `${goalPercent}%` : 'Not set'}
              </div>
            </div>
            <div style={{
              width: '100%',
              height: '10px',
              backgroundColor: 'var(--md-sys-color-surface-container)',
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{
                width: `${overallPercent}%`,
                height: '100%',
                backgroundColor: 'var(--md-sys-color-primary)',
                borderRadius: '6px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '14px' }}>
              {overallPercent}% complete
            </div>
            <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px', marginTop: '8px' }}>
              {predictionPercent}% up to today
            </div>
          </md-elevated-card>
        )}

        {/* Rankings List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {rankings.map((participant, index) => {
            const progressString = participant.progressString || "";
            const showFire = shouldShowCooperativeStreak(datesToChapters, progressString, today);
            return (
              <md-elevated-card 
                key={participant.userId || index}
                style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  backgroundColor: !isCooperative && index < 3
                    ? 'var(--md-sys-color-surface-container-high)'
                    : 'var(--md-sys-color-surface-container-low)',
                  border: !isCooperative && index < 3 ? '2px solid var(--md-sys-color-primary)' : 'none'
                }}
              >
                {!isCooperative && (
                  <div style={{
                    minWidth: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: index < 3 ? 'var(--md-sys-color-primary-container)' : 'var(--md-sys-color-surface-container)',
                    color: index < 3 ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    borderRadius: '50%',
                    fontSize: index < 3 ? '24px' : '16px',
                    fontWeight: '600'
                  }}>
                    {getRankDisplay(index)}
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: 'var(--md-sys-color-on-surface)',
                    marginBottom: isCooperative && isChallengeAdmin ? '4px' : '0'
                  }}>
                    {participant.name}
                  </div>
                  {isCooperative && isChallengeAdmin && (
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{participant.progress} / {totalPortions} completed</span>
                      <span>•</span>
                      <span>{participant.percentage}%</span>
                    </div>
                  )}
                  {!isCooperative && (
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{participant.progress} / {totalPortions} completed</span>
                      <span>•</span>
                      <span>{participant.percentage}%</span>
                    </div>
                  )}
                </div>

                {isCooperative && (
                  <div style={{
                    minWidth: '24px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {showFire && (
                      <Icon name="streak" style={{ color: 'var(--md-sys-color-error)' }} />
                    )}
                  </div>
                )}

                {!isCooperative && (
                  <>
                    <div style={{
                      width: '80px',
                      height: '8px',
                      backgroundColor: 'var(--md-sys-color-surface-container)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${participant.percentage}%`,
                        height: '100%',
                        backgroundColor: index < 3 ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-secondary)',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>

                    <div style={{
                      minWidth: '48px',
                      textAlign: 'right',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: index < 3 ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface)'
                    }}>
                      {participant.percentage}%
                    </div>
                  </>
                )}
              </md-elevated-card>
            );
          })}
        </div>

        {/* Empty State */}
        {rankings.length === 0 && loadedChallenges && (
          <div style={{
            textAlign: 'center',
            padding: '32px 16px',
            color: 'var(--md-sys-color-on-surface-variant)'
          }}>
            <Icon name="leaderboard" size="hero" style={{ marginBottom: '16px' }} />
            <p style={{ margin: '0', fontSize: '16px' }}>
              No participants yet
            </p>
          </div>
        )}
        </div>
      </div>
    </>
  );
};
